import React, { useEffect, useState, useCallback } from 'react';
import axios from '../utils/axios';

const PRODUCT_LABELS = {
  PERSONAL_LIFESTYLE: 'Personal / Lifestyle Lens',
  STUDENT_SUCCESS: 'Student Success Lens',
  PROFESSIONAL_LEADERSHIP: 'Professional / Leadership Lens',
  FAMILY_ECOSYSTEM: 'Family Ecosystem Lens',
  COSMIC_BUNDLE: 'Cosmic Integration Bundle',
  FINANCIAL_DEEP_DIVE: 'Financial Deep-Dive',
  HEALTH_DEEP_DIVE: 'Health & Fitness Deep-Dive',
  COMPATIBILITY: 'Compatibility Report',
};

const PRODUCT_PRICES = {
  PERSONAL_LIFESTYLE: '$29.99',
  STUDENT_SUCCESS: '$29.99',
  PROFESSIONAL_LEADERSHIP: '$29.99',
  FAMILY_ECOSYSTEM: '$29.99',
  COSMIC_BUNDLE: '$99.99',
  FINANCIAL_DEEP_DIVE: '$29.99',
  HEALTH_DEEP_DIVE: '$29.99',
  COMPATIBILITY: '$49.99',
};

const ITEMS_PER_PAGE = 5;

const BillingHistory = ({ userEmail, onBack }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchBillingData = useCallback(async () => {
    if (!userEmail) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const resp = await axios.get(`/api/v1/user/${encodeURIComponent(userEmail)}/reports`);
      if (resp.data?.success) {
        const allReports = resp.data.reports || [];
        // Filter to only reports with purchases
        const paidReports = allReports.filter(
          (r) => r.paid_products && r.paid_products.length > 0
        );
        setReports(paidReports);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load billing history');
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Count total purchases across all reports
  const totalPurchases = reports.reduce(
    (sum, r) => sum + (r.paid_products?.length || 0),
    0
  );

  // Pagination
  const totalPages = Math.ceil(reports.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentReports = reports.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen px-3 py-6 sm:p-4 sm:py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          {onBack && (
            <button
              onClick={onBack}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-semibold transition-colors mb-3"
            >
              ← Back to Dashboard
            </button>
          )}
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Billing History
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Your purchases and unlocked report lenses
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm">Loading billing history…</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <p className="text-red-700 text-sm mb-3">{error}</p>
            <button
              onClick={fetchBillingData}
              className="text-sm text-red-600 hover:text-red-800 font-semibold underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && reports.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="text-4xl mb-3">🧾</div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              No Purchases Yet
            </h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              When you unlock report lenses or purchase the Cosmic Bundle,
              your billing history will appear here.
            </p>
          </div>
        )}

        {/* Summary Card */}
        {!loading && !error && reports.length > 0 && (
          <>
            <div className="bg-gradient-to-br from-indigo-700 via-purple-700 to-indigo-900 rounded-2xl shadow-xl p-5 sm:p-6 text-white mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-indigo-200 text-xs uppercase tracking-wider mb-1">
                    Total Purchases
                  </p>
                  <p className="text-2xl font-bold">{totalPurchases}</p>
                </div>
                <div className="text-right">
                  <p className="text-indigo-200 text-xs uppercase tracking-wider mb-1">
                    Assessments with Unlocks
                  </p>
                  <p className="text-2xl font-bold">{reports.length}</p>
                </div>
              </div>
            </div>

            {/* Purchase List */}
            <div className="space-y-4 mb-6">
              {currentReports.map((report) => (
                <div
                  key={report.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
                >
                  {/* Report Header */}
                  <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-500">Assessment</p>
                      <p className="text-sm font-semibold text-gray-800">
                        {report.id?.slice(0, 8)}…
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Date</p>
                      <p className="text-sm font-medium text-gray-700">
                        {formatDate(report.created_at || report.submitted_at)}
                      </p>
                    </div>
                  </div>

                  {/* Products */}
                  <div className="p-5">
                    <ul className="space-y-3">
                      {(report.paid_products || []).map((product) => (
                        <li
                          key={product}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 text-sm">
                              ✓
                            </span>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {PRODUCT_LABELS[product] || product}
                              </p>
                              <p className="text-xs text-gray-500">Unlocked</p>
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-gray-700">
                            {PRODUCT_PRICES[product] || '—'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ← Previous
                </button>
                
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BillingHistory;
